/**
 * The editor's tool palette (asked for 2026-10-10): the right-hand panel as a
 * column of tools, each opening and closing with one click, with a line saying
 * where it stands when it is closed, so only what is being used takes room.
 * Which ones are open is remembered on this device.
 *
 * Arranged again 2026-10-11: Pictures (the zone's picture and its look, the
 * whole card in black and white, this card's pictures and below them the
 * library), Shape (zones 10-13, 2026-10-11), Message, All zones, Paper. A picture's position and size are no
 * longer here: a click on a zone opens them in a pop-up under the card.
 *
 * 2026-10-17: the frame's own pop-up (editor.tsx) took the choosing of its picture, its look and
 * position; "Open all / Close all" gave way to an arrow that folds the whole panel to the side.
 */
import { useState, type ReactNode } from 'react';

export type SectionId = 'picture' | 'shape' | 'message' | 'zones' | 'invite' | 'paper';
export const SECTIONS: SectionId[] = ['picture', 'shape', 'message', 'zones', 'invite', 'paper'];
const KEY = 'fmc.palette';
const DEFAULT_OPEN: SectionId[] = ['picture', 'shape', 'message'];

/** The open sections, remembered (a convenience only: a private window starts with the defaults). */
export function useOpenSections() {
  const [open, setOpenState] = useState<Set<SectionId>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (Array.isArray(saved)) return new Set(saved.filter((s): s is SectionId => SECTIONS.includes(s)));
    } catch { /* the defaults */ }
    return new Set(DEFAULT_OPEN);
  });
  const save = (next: Set<SectionId>) => {
    setOpenState(next);
    try { localStorage.setItem(KEY, JSON.stringify([...next])); } catch { /* a preference only */ }
  };
  return {
    isOpen: (id: SectionId) => open.has(id),
    toggle: (id: SectionId) => {
      const next = new Set(open);
      if (next.has(id)) next.delete(id); else next.add(id);
      save(next);
    },
    /** Open one (and bring it into view): "Choose a picture" under the card uses it. */
    show: (id: SectionId) => {
      if (!open.has(id)) save(new Set([...open, id]));
      // a moment, for a panel that was folded away to come back first
      setTimeout(() => document.getElementById(`pal-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 60);
    },
    all: (on: boolean) => save(new Set(on ? SECTIONS : [])),
  };
}

/** Small line icons, one per tool: drawn here, so they take the text colour and need no files. */
const ICONS: Record<SectionId, ReactNode> = {
  picture: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 16l5-5 4 4 3-3 6 6" /><circle cx="16" cy="9" r="1.6" /></>,
  shape: <><path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" /></>,
  message: <><path d="M4 6h16M4 11h16M4 16h10" /><path d="M17 15l3 3-3 3" /></>,
  zones: <><path d="M4 4h16v16H4z" /><path d="M4 4l16 16M20 4L4 20" /></>,
  paper: <><path d="M6 3h9l3 3v15H6z" /><path d="M15 3v3h3" /><path d="M9 11h6M9 15h6" /></>,
  invite: <><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 8l9 6 9-6" /><path d="M8 3v4M16 3v4" /></>,
};

/** A section's icon on its own: the folded-away panel's buttons (2026-10-17). */
export function SectionIcon({ id }: { id: SectionId }) {
  return <svg className="pal-icon" viewBox="0 0 24 24" aria-hidden="true">{ICONS[id]}</svg>;
}

export function PaletteSection({ id, title, status, open, onToggle, disabled = false, children }: {
  id: SectionId; title: string; status?: ReactNode; open: boolean; onToggle: () => void; disabled?: boolean; children: ReactNode;
}) {
  const shut = disabled || !open;
  return (
    <section className={`pal-sec${shut ? '' : ' open'}${disabled ? ' disabled' : ''}`} id={`pal-${id}`}>
      <h3 className="pal-head">
        <button aria-expanded={!shut} aria-controls={`pal-body-${id}`} onClick={onToggle} disabled={disabled}>
          <svg className="pal-icon" viewBox="0 0 24 24" aria-hidden="true">{ICONS[id]}</svg>
          <span className="pal-title">{title}</span>
          {status !== undefined && <span className="pal-status">{status}</span>}
          <svg className="pal-chev" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
        </button>
      </h3>
      {!shut && <div className="pal-body" id={`pal-body-${id}`}>{children}</div>}
    </section>
  );
}
